/**
 * Parse an event date string to a local Date object.
 * Handles both YYYY-MM-DD strings and ISO datetime strings (e.g., from pg driver).
 * The backend stores DATE columns at midnight CET/CEST, which serializes to UTC
 * with a timezone offset (e.g., "2026-04-15T22:00:00.000Z" for April 16 CET).
 * This function ensures the correct date is returned regardless of client timezone.
 */
export function parseEventDate(
	date: string | Date | undefined | null
): Date | null {
	if (!date) return null;
	if (date instanceof Date) {
		if (isNaN(date.getTime())) return null;
		return date;
	}
	// YYYY-MM-DD format: parse as local date directly
	if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
		const [year, month, day] = date.split("-").map(Number);
		return new Date(year, month - 1, day);
	}
	// ISO datetime string (e.g., "2026-04-15T22:00:00.000Z")
	// Extract the date in Europe/Berlin timezone (where the backend runs)
	const d = new Date(date);
	if (isNaN(d.getTime())) return null;
	const parts = new Intl.DateTimeFormat("en", {
		timeZone: "Europe/Berlin",
		year: "numeric",
		month: "numeric",
		day: "numeric",
	}).formatToParts(d);
	const year = parseInt(parts.find((p) => p.type === "year")!.value);
	const month = parseInt(parts.find((p) => p.type === "month")!.value);
	const day = parseInt(parts.find((p) => p.type === "day")!.value);
	return new Date(year, month - 1, day);
}

/**
 * Calculate the number of days remaining from today to a given date
 * @param eventDate - The target date (as string or Date object)
 * @returns A human-readable string describing the time difference
 */
export function calculateDaysRemaining(
	eventDate: string | Date | undefined
): string {
	if (!eventDate) return "Datum noch offen";

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const event = parseEventDate(eventDate) || new Date();
	event.setHours(0, 0, 0, 0);

	const diffTime = event.getTime() - today.getTime();
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

	if (diffDays < 0) {
		return `Vor ${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? "Tag" : "Tagen"}`;
	} else if (diffDays === 0) {
		return "Heute";
	} else if (diffDays === 1) {
		return "Morgen";
	} else {
		return `In ${diffDays} ${diffDays === 1 ? "Tag" : "Tagen"}`;
	}
}

/**
 * Format a date string to a readable format
 * @param dateString - The date string to format (ISO format YYYY-MM-DD or Date object)
 * @param startTime - Optional start time to create accurate timezone-aware date
 * @returns Formatted date string
 */
export function formatDate(
	dateString: string | Date,
	startTime?: string
): string {
	let date: Date;

	// If we have both date and time, create a proper datetime
	if (
		typeof dateString === "string" &&
		/^\d{4}-\d{2}-\d{2}$/.test(dateString) &&
		startTime
	) {
		// Backend sends date as YYYY-MM-DD and time as HH:MM:SS
		const dateTimeString = `${dateString}T${startTime}`;
		// This creates a date in the local timezone interpretation of the Frankfurt time
		date = new Date(dateTimeString);
	} else if (
		typeof dateString === "string" &&
		/^\d{4}-\d{2}-\d{2}$/.test(dateString)
	) {
		// Just date without time
		const dateTimeString = `${dateString}T00:00:00`;
		date = new Date(dateTimeString);
	} else {
		// For other date strings or Date objects, parse in Europe/Berlin timezone
		date =
			parseEventDate(dateString) ||
			(typeof dateString === "string" ? new Date(dateString) : dateString);
	}

	return date.toLocaleDateString("de-DE", {
		weekday: "short",
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

/**
 * Format a date to ISO date string (YYYY-MM-DD) in UTC
 * WARNING: This keeps the UTC date, which may differ from local date
 * @param date - The date to format
 * @returns ISO date string in UTC
 */
export function toISODateString(date: string | Date): string {
	const d = typeof date === "string" ? new Date(date) : date;
	return d.toISOString().split("T")[0];
}

/**
 * Format a date to local date string (YYYY-MM-DD) in user's timezone
 * This is the preferred method for displaying dates to users
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format in local timezone
 */
export function toLocalDateString(
	date: string | Date | undefined | null
): string {
	if (!date) return "";

	try {
		let d: Date;

		if (date instanceof Date) {
			d = date;
		}
		// If it's a string in YYYY-MM-DD format (date only, no time)
		else if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
			// Parse as local date to avoid timezone issues
			const [year, month, day] = date.split("-").map(Number);
			d = new Date(year, month - 1, day);
		}
		else {
			// For datetime strings or other formats, parse in Europe/Berlin timezone
			d = parseEventDate(date) || new Date(date);
		}

		// Check if date is valid
		if (isNaN(d.getTime())) {
			console.error("Invalid date provided to toLocalDateString:", date);
			return "";
		}

		// Get the year, month, and day in local timezone
		const year = d.getFullYear();
		const month = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");

		// Ensure year is always 4 digits by padding with zeros
		const yearStr =
			year >= 0 && year <= 9999 ? String(year).padStart(4, "0") : String(year);

		// Return in YYYY-MM-DD format
		return `${yearStr}-${month}-${day}`;
	} catch (error) {
		console.error("Error formatting date in toLocalDateString:", error, date);
		return "";
	}
}

/**
 * Format a date for display with full details (e.g., "Montag, den 5. November 2025")
 * @param date - The date to format
 * @returns Formatted date string for display
 */
export function formatDateDisplay(
	date: string | Date | undefined | null
): string {
	if (!date) return "Datum noch offen";
	let d: Date;
	if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
		const [year, month, day] = date.split("-").map(Number);
		d = new Date(year, month - 1, day);
	} else {
		d =
			parseEventDate(date) ||
			(typeof date === "string" ? new Date(date) : date);
	}
	const formatted = d.toLocaleDateString("de-DE", {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	});
	// Add "den" after the weekday (e.g., "Montag, 5. November 2025" -> "Montag, den 5. November 2025")
	return formatted.replace(/^(\w+), /, "$1, den ");
}

/**
 * Format a time string (HH:MM:SS) to a more readable format
 * @param timeString - Time in HH:MM:SS format
 * @returns Formatted time string in 24-hour format (e.g., "14:30")
 */
export function formatTime(timeString: string | undefined | null): string {
	if (!timeString) return "";

	// Parse time string (HH:MM:SS or HH:MM)
	const [hours, minutes] = timeString.split(":").map(Number);

	// Format as HH:MM in 24-hour format
	const formattedHours = hours.toString().padStart(2, "0");
	const formattedMinutes = minutes.toString().padStart(2, "0");

	return `${formattedHours}:${formattedMinutes}`;
}

/**
 * Format a time range for display
 * @param startTime - Start time in HH:MM:SS format
 * @param endTime - End time in HH:MM:SS format
 * @returns Formatted time range in 24-hour format (e.g., "08:00 - 16:00")
 */
export function formatTimeRange(
	startTime: string | undefined,
	endTime: string | undefined
): string {
	if (!startTime || !endTime) return "Zeit noch offen";
	return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}

/**
 * Format date and time together for display
 * @param date - The date
 * @param startTime - Start time in HH:MM:SS format
 * @param endTime - End time in HH:MM:SS format
 * @returns Formatted date and time string
 */
export function formatDateTime(
	date: string | Date | undefined | null,
	startTime?: string,
	endTime?: string
): string {
	const dateStr = formatDateDisplay(date);
	if (startTime && endTime) {
		return `${dateStr} • ${formatTimeRange(startTime, endTime)}`;
	}
	return dateStr;
}

/**
 * Check if a date is today
 * @param date - The date to check
 * @returns true if the date is today
 */
export function isToday(date: string | Date): boolean {
	const d =
		parseEventDate(date) ||
		(typeof date === "string" ? new Date(date) : date);
	const today = new Date();
	return (
		d.getDate() === today.getDate() &&
		d.getMonth() === today.getMonth() &&
		d.getFullYear() === today.getFullYear()
	);
}

/**
 * Check if a date is in the past
 * @param date - The date to check
 * @returns true if the date is in the past
 */
export function isPastDate(date: string | Date): boolean {
	const d =
		parseEventDate(date) ||
		(typeof date === "string" ? new Date(date) : date);
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	d.setHours(0, 0, 0, 0);
	return d < today;
}
